import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import AreaSelect from '../components/AreaSelect';
import { escortApi, driverApi, absoluteUrl } from '../api/mockApi';
import Avatar from '../components/Avatar';
import Attachments from '../components/Attachments';

const QUALIFICATIONS = ['DBS', 'First Aid', 'Safeguard', 'PATS'];
const LICENSE_OPTIONS = ['PCO', 'LOCAL AUTHORITY', 'TAXI BADGE', 'PSV'];

const formatCoord = (value) => {
  const num = Number(value);
  return value === null || value === undefined || value === '' || !Number.isFinite(num)
    ? 'Not set'
    : num.toFixed(5);
};

const EscortDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [escort, setEscort] = useState(null);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [driverName, setDriverName] = useState('');
  const [nearby, setNearby] = useState(null);
  const [nearbyLoading, setNearbyLoading] = useState(true);
  const [radius, setRadius] = useState(5);
  const [radiusInput, setRadiusInput] = useState('5');
  const [unassignedOnly, setUnassignedOnly] = useState(false);

  // Drivers nearest to this PA. Anchored on the PA's coordinates, which the
  // server derives from their post code (and will prefer live GPS once the app
  // reports it), so this works without any change to the mobile app.
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setNearbyLoading(true);
      const res = await escortApi.nearestDrivers(id, {
        radius,
        limit: 10,
        unassignedOnly: unassignedOnly ? 'true' : '',
      });
      if (cancelled) return;
      setNearby(res && res.success && res.data
        ? { ...res.data, drivers: Array.isArray(res.data.drivers) ? res.data.drivers : [] }
        : { drivers: [], radiusMiles: radius, withinRadius: 0, totalWithLocation: 0 });
      setNearbyLoading(false);
    };
    load();
    return () => { cancelled = true; };
  }, [id, radius, unassignedOnly]);

  useEffect(() => {
    const load = async () => {
      const res = await escortApi.getById(id);
      if (res.success) {
        setEscort(res.data);
        setForm(res.data);
      }
    };
    load();
  }, [id]);

  // The list endpoint resolves belongsToDriverName for us; when only an id is
  // present (e.g. straight after an update) fetch the driver to show a name.
  useEffect(() => {
    const driverId = escort && (escort.belongsToDriver || escort.assignedDriver);
    if (!driverId) {
      setDriverName('');
      return undefined;
    }
    if (escort.belongsToDriverName) {
      setDriverName(escort.belongsToDriverName);
      return undefined;
    }
    let cancelled = false;
    const loadDriver = async () => {
      const res = await driverApi.getById(driverId);
      if (!cancelled && res.success && res.data) setDriverName(res.data.fullName || '');
    };
    loadDriver();
    return () => { cancelled = true; };
  }, [escort]);

  const handleSave = async (e) => {
    e.preventDefault();
    // Drop the server-computed fields the record was loaded with so the API can
    // re-geocode. An area the admin typed is kept; one merely inherited from the
    // loaded record is cleared when the post code moved, so it is derived again.
    const { latitude, longitude, routeName, belongsToDriverName, ...rest } = form;
    const postCodeChanged = (form.postCode || '') !== (escort.postCode || '');
    const areaEdited = (form.area || '') !== (escort.area || '');
    const res = await escortApi.update(id, {
      ...rest,
      area: postCodeChanged && !areaEdited ? '' : (form.area || ''),
    });
    if (res.success) {
      setEscort(res.data);
      setForm(res.data);
      setEditing(false);
    }
  };

  const handleStatusToggle = async () => {
    const newStatus = escort.routeStatus === 'ON ROUTE' ? 'OFF ROUTE' : 'ON ROUTE';
    const res = await escortApi.updateRouteStatus(id, newStatus);
    if (res.success) setEscort(res.data);
  };

  const handleDelete = async () => {
    if (window.confirm('Delete this passenger assistant?')) {
      await escortApi.delete(id);
      navigate('/escorts');
    }
  };

  const toggleQual = (q) => {
    setForm(prev => ({
      ...prev,
      qualifications: prev.qualifications?.includes(q)
        ? prev.qualifications.filter(x => x !== q)
        : [...(prev.qualifications || []), q],
    }));
  };

  const handleDownload = (url, name) => {
    const href = absoluteUrl(url);
    if (!href) return;
    const a = document.createElement("a");
    a.href = href;
    a.download = name || 'document';
    a.target = '_blank';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  if (!escort) return <div className="loading">Loading...</div>;

  const linkedDriverId = escort.belongsToDriver || escort.assignedDriver || null;

  return (
    <div>
      <Link to="/escorts" className="back-link">← Back to Passenger Assistants</Link>

      <div className="page-header">
        <div className="title-with-avatar">
          <Avatar name={escort.fullName} photoUrl={escort.photoUrl} size={64} />
          <div>
            <h1 className="page-title" style={{ marginBottom: 2 }}>{escort.fullName || 'PA Details'}</h1>
            <span className={`distance-badge ${escort.photoUrl ? 'within' : 'outside'}`}>
              {escort.photoUrl ? 'Verification selfie on file' : 'Awaiting verification selfie'}
            </span>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 12 }}>
          <button className="btn btn-outline btn-sm" onClick={handleStatusToggle}>
            Switch to {escort.routeStatus === 'ON ROUTE' ? 'OFF ROUTE' : 'ON ROUTE'}
          </button>
          <button className="btn btn-gold btn-sm" onClick={() => setEditing(true)}>Edit</button>
          <button className="btn btn-danger btn-sm" onClick={handleDelete}>Delete</button>
        </div>
      </div>

      {!editing ? (
        <div className="detail-grid">
          <div className="detail-card">
            <h3>Personal Details</h3>
            <div className="detail-row"><span className="detail-label">Full Name</span><span className="detail-value">{escort.fullName || 'N/A'}</span></div>
            <div className="detail-row"><span className="detail-label">Address</span><span className="detail-value">{escort.address || 'N/A'}</span></div>
            <div className="detail-row"><span className="detail-label">Post Code</span><span className="detail-value">{escort.postCode || 'N/A'}</span></div>
            <div className="detail-row"><span className="detail-label">Area</span><span className="detail-value">{escort.area || 'N/A'}</span></div>
            <div className="detail-row"><span className="detail-label">Mobile</span><span className="detail-value">{escort.mobileNumber}</span></div>
          </div>
          <div className="detail-card">
            <h3>Route Status</h3>
            <span className={`status-badge ${escort.routeStatus === 'ON ROUTE' ? 'on-route' : 'off-route'}`} style={{ fontSize: 16, padding: '8px 20px' }}>
              <span className="dot"></span>
              {escort.routeStatus}
            </span>
            <div className="detail-row" style={{ marginTop: 14 }}>
              <span className="detail-label">Route</span>
              <span className="detail-value">{escort.routeName || 'Not assigned'}</span>
            </div>
          </div>
          <div className="detail-card">
            <h3>Location</h3>
            <div className="detail-row"><span className="detail-label">Area</span><span className="detail-value">{escort.area || 'N/A'}</span></div>
            <div className="detail-row"><span className="detail-label">Latitude</span><span className="detail-value">{formatCoord(escort.latitude)}</span></div>
            <div className="detail-row"><span className="detail-label">Longitude</span><span className="detail-value">{formatCoord(escort.longitude)}</span></div>
          </div>
          <div className="detail-card">
            <h3>Qualifications</h3>
            <div className="qual-badges">
              {QUALIFICATIONS.map(q => (
                <span key={q} className={`qual-badge ${escort.qualifications?.includes(q) ? 'active' : 'inactive'}`}>{q}</span>
              ))}
            </div>
          </div>
          <div className="detail-card">
            <h3>License</h3>
            <p style={{ color: '#CAB069' }}>{escort.licenses || 'Not set'}</p>
          </div>
          <div className="detail-card">
            <h3>Assigned Driver</h3>
            {linkedDriverId ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                <Link to={`/drivers/${linkedDriverId}`} style={{ color: '#DBAD17', fontWeight: 600, textDecoration: 'none' }}>
                  {driverName || 'View driver'}
                </Link>
                {escort.belongsToDriver ? <span className="pill">Added with this driver</span> : null}
              </div>
            ) : (
              <p style={{ color: '#818181', fontStyle: 'italic' }}>Not assigned to any driver</p>
            )}
          </div>
          <div className="detail-card">
            <h3>Documents</h3>
            {escort.attachments?.length ? (
              <Attachments attachments={escort.attachments} onDownload={handleDownload} />
            ) : (
              <p style={{ color: '#818181', fontStyle: 'italic' }}>No documents uploaded</p>
            )}
          </div>

          {/* Drivers nearest to this PA */}
          <div className="detail-card" style={{ gridColumn: '1 / -1' }}>
            <h3>Nearby Drivers</h3>
            <div className="radius-control" style={{ marginBottom: 14 }}>
              <label htmlFor="pa-radius" style={{ fontSize: 13, color: 'var(--text-light)' }}>Radius (miles)</label>
              <input
                id="pa-radius"
                type="number"
                min="0.5"
                max="50"
                step="0.5"
                value={radiusInput}
                onChange={e => setRadiusInput(e.target.value)}
              />
              <button
                className="btn btn-outline btn-sm"
                onClick={() => {
                  const n = Number(radiusInput);
                  setRadius(Number.isFinite(n) && n > 0 ? n : 5);
                }}
              >
                Apply
              </button>
              <label style={{ fontSize: 13, color: 'var(--text-light)', marginLeft: 12 }}>
                <input
                  type="checkbox"
                  checked={unassignedOnly}
                  onChange={e => setUnassignedOnly(e.target.checked)}
                  style={{ marginRight: 6 }}
                />
                Unassigned only
              </label>
            </div>

            {nearbyLoading ? (
              <p className="empty-state">Finding nearby drivers…</p>
            ) : nearby && nearby.reason ? (
              <p className="empty-state">{nearby.reason}</p>
            ) : nearby && nearby.drivers.length === 0 ? (
              <p className="empty-state">No drivers with a known location match these filters.</p>
            ) : nearby ? (
              <>
                <p className="coverage-meta">
                  {nearby.withinRadius} of {nearby.drivers.length} shown are within {nearby.radiusMiles} miles
                  {' · '}{nearby.totalWithLocation} drivers have a location on file
                </p>
                {nearby.drivers.map(d => (
                  <div className="nearest-item" key={d.id}>
                    <div className="who">
                      <span className="nearest-name">{d.fullName || 'Unnamed'}</span>
                      <span className="nearest-sub">
                        {[d.area, d.vehicle, d.routeStatus].filter(Boolean).join(' · ')}
                      </span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <span className={`distance-badge ${d.withinRadius ? 'within' : 'outside'}`}>
                        {Number(d.distanceMiles).toFixed(1)} mi
                      </span>
                      <button className="btn btn-outline btn-sm" onClick={() => navigate(`/drivers/${d.id}`)}>
                        View
                      </button>
                    </div>
                  </div>
                ))}
              </>
            ) : null}
          </div>
        </div>
      ) : (
        <div className="modal" style={{ maxWidth: 600, margin: '0 auto' }}>
          <h3>Edit Passenger Assistant</h3>
          <form onSubmit={handleSave}>
            <div className="form-group">
              <label>Full Name</label>
              <input value={form.fullName || ''} onChange={e => setForm({...form, fullName: e.target.value})} />
            </div>
            <div className="form-group">
              <label>Address</label>
              <input value={form.address || ''} onChange={e => setForm({...form, address: e.target.value})} />
            </div>
            <div className="form-group">
              <label>Post Code</label>
              <input value={form.postCode || ''} onChange={e => setForm({...form, postCode: e.target.value})} />
            </div>
            <div className="form-group">
              <label>Area</label>
              <AreaSelect
                  value={form.area || ''}
                  onChange={v => setForm({ ...form, area: v })}
                  blankLabel="Detect from post code"
                />
            </div>
            <div className="form-group">
              <label>Qualifications</label>
              <div className="checkbox-group">
                {QUALIFICATIONS.map(q => (
                  <div key={q} className={`checkbox-item ${form.qualifications?.includes(q) ? 'checked' : ''}`} onClick={() => toggleQual(q)}>
                    {form.qualifications?.includes(q) ? '✓' : '○'} {q}
                  </div>
                ))}
              </div>
            </div>
            <div className="form-group">
              <label>License</label>
              <select value={form.licenses || ''} onChange={e => setForm({...form, licenses: e.target.value})}>
                <option value="">Select license</option>
                {LICENSE_OPTIONS.map(l => <option key={l} value={l}>{l}</option>)}
              </select>
            </div>
            <div className="modal-actions">
              <button type="button" className="btn btn-outline" onClick={() => setEditing(false)}>Cancel</button>
              <button type="submit" className="btn btn-gold">Save</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default EscortDetailPage;
